// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tag } from 'antd';
import { DateTime } from 'luxon';
import { useParams, useNavigate } from 'react-router-dom';
import {
  List,
  Col,
  Row,
  Card,
  Avatar,
  Divider,
  Button,
  Dropdown,
  MenuProps,
} from 'antd';
import { useUserStore } from '../../store/user-store';
import { Sprite } from '../sprite';
import { Role } from '../../services/auth-service/types';
import { useAuthStore } from '../../store/auth-store';
import useAlertStore from '../../store/alert';
import { useAppContext } from '../../App';
import StringUtils, { errorMessage } from '../../utils';

interface Props {}

interface RowProps {
  label: string;
  value: any;
  type?: string;
}

type fieldT =
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'birthDate'
  | 'gender'
  | 'phone'
  | 'roles'
  | 'createdAt'
  | 'address';
interface userDetailsMappingI {
  type: string;
  label: string;
  field: fieldT;
}

export const CustomerDetails: React.FC<Props> = () => {
  const { alert } = useAlertStore();
  const { id } = useParams();
  const {
    currentUser,
    deleteUser,
    getUserDetails,
    loadingUsers,
    clearCurrentUser,
  } = useUserStore();
  const [personalInfo, setPersonalInfo] = useState<RowProps[]>([]);
  const [accountInfo, setAccountInfo] = useState<RowProps[]>([]);
  const [addressInfo, setAddressInfo] = useState<RowProps[]>([]);

  const { globalNotif } = useAppContext();
  const navigate = useNavigate();
  const { idToken } = useAuthStore();
  const cUser = useMemo(() => idToken?.payload, [idToken]);

  useEffect(() => {
    getUserDetails(Number(id));
  }, [id, getUserDetails]);

  const getValue = useCallback(
    (field: fieldT) => {
      let toReturn = null;
      if (currentUser) {
        switch (field) {
          case 'gender':
            if (['M', 'm', 'Male', 'male'].includes(currentUser[field])) {
              toReturn = 'Male';
            } else if (
              ['F', 'f', 'Female', 'female'].includes(currentUser[field])
            ) {
              toReturn = 'Female';
            } else {
              toReturn = 'Unknown';
            }
            break;
          case 'birthDate':
            toReturn = currentUser[field]
              ? DateTime.fromFormat(currentUser[field], 'MM/dd/yyyy').toFormat(
                  'MM-dd-yyyy',
                )
              : 'Unknown';
            break;
          case 'createdAt':
            toReturn = DateTime.fromISO(currentUser[field]).toFormat(
              'MM-dd-yyyy',
            );
            break;
          case 'roles':
            toReturn = 'Customer';
            if (currentUser[field].includes('admin')) {
              toReturn = 'Admin';
            } else if (currentUser[field].includes('superuser')) {
              toReturn = 'Superuser';
            } else if (currentUser[field].includes('physician')) {
              toReturn = 'Physician';
            }
            break;
          default:
            toReturn = currentUser[field];
            break;
        }
      }
      return toReturn;
    },
    [currentUser],
  );

  const userDetailsMapping = useMemo<userDetailsMappingI[]>(
    () => [
      { type: 'personalInfo', label: 'First Name', field: 'firstName' },
      { type: 'personalInfo', label: 'Middle Name', field: 'middleName' },
      { type: 'personalInfo', label: 'Last Name', field: 'lastName' },
      { type: 'personalInfo', label: 'Date of Birth', field: 'birthDate' },
      { type: 'personalInfo', label: 'Gender', field: 'gender' },
      { type: 'personalInfo', label: 'Contact no.', field: 'phone' },
      { type: 'accountInfo', label: 'Access Type', field: 'roles' },
      { type: 'accountInfo', label: 'Date joined', field: 'createdAt' },
      { type: 'addressInfo', label: 'Address', field: 'address' },
    ],
    [],
  );

  useEffect(() => {
    if (currentUser) {
      const pInfo: RowProps[] = [];
      const acInfo: RowProps[] = [];
      const adInfo: RowProps[] = [];
      userDetailsMapping.forEach(row => {
        const item = {
          label: row.label,
          value: getValue(row.field),
        };
        switch (row.type) {
          case 'personalInfo':
            pInfo.push(item);
            break;
          case 'accountInfo':
            acInfo.push(item);
            break;
          case 'addressInfo':
            adInfo.push(item);
            break;
          default:
            break;
        }
      });
      setPersonalInfo(pInfo);
      setAccountInfo(acInfo);
      setAddressInfo(adInfo);
    }
  }, [currentUser, getValue, userDetailsMapping]);

  const fullName = useMemo(
    () => `${currentUser?.firstName} ${currentUser?.lastName}`,
    [currentUser],
  );
  const descriptionText = useMemo(
    () =>
      `${currentUser?.email} • Joined ${DateTime.fromISO(
        currentUser?.createdAt || '',
      ).toFormat('MMM dd, yyyy')}`,
    [currentUser],
  );

  useEffect(
    () => () => {
      clearCurrentUser();
    },
    [clearCurrentUser],
  );

  const userRoles = useMemo<Role[]>(() => {
    if (!cUser?.['custom:roles']) return [];
    const roles = cUser['custom:roles'].split(',') as Role[];
    return roles;
  }, [cUser]);

  const canEditProfile = useMemo(() => {
    const roles = userRoles;
    const rolesAccess = [Role.SuperUser, Role.Admin];
    let canEdit = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canEdit = true;
      }
    });
    return canEdit;
  }, [userRoles]);

  const doDelete = useCallback(() => {
    if (!id) {
      return;
    }
    alert(
      'Delete user',
      `Are you sure you want to delete this user? This will permanently delete the user and order data associated with this user. Their access to the HeartMap mobile app will also be removed. 

<i>Note: This cannot be undone. Once deleted, it cannot be recovered.</i>`,
      [
        { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
        {
          text: 'Yes',
          onClick: async () => {
            try {
              await deleteUser({ id });
              globalNotif?.success({ message: 'User successfully deleted.' });
              navigate('/customers');
            } catch (err) {
              globalNotif?.error({ message: errorMessage(err) });
            }
          },
        },
      ],
    );
  }, [alert, deleteUser, globalNotif, id, navigate]);

  const handleMenuClick: MenuProps['onClick'] = e => {
    if (e.key === 'edit') {
      navigate(`/customers/${id}/edit`);
    } else if (e.key === 'delete') {
      doDelete();
    }
  };

  const items: MenuProps['items'] = useMemo(
    () =>
      [
        {
          label: 'Edit Profile',
          key: 'edit',
        },
        {
          label: 'Delete Profile',
          key: 'delete',
        },
      ].filter(
        e =>
          (e.key === 'delete' && userRoles.includes(Role.SuperUser)) ||
          e.key !== 'delete',
      ),
    [userRoles],
  );

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Card
      title={
        <div className="py-5">
          <Row gutter={10}>
            {currentUser && (
              <Col>
                <Avatar
                  style={{
                    backgroundColor: '#003E74',
                    verticalAlign: 'middle',
                  }}
                  size={50}
                  gap={1}>
                  {`${
                    currentUser?.firstName
                      ? currentUser?.firstName[0]?.toLocaleUpperCase()
                      : ''
                  }
                    ${
                      currentUser?.lastName
                        ? currentUser?.lastName[0]?.toLocaleUpperCase()
                        : ''
                    }`}
                </Avatar>
              </Col>
            )}
            <Col>
              <div className="flex flex-row items-center">
                <div className="text-lg flex flex-col">{fullName}</div>
                <div className="flex flex-col pl-1">
                  <Tag>
                    {StringUtils.capitalize(
                      (currentUser?.registeredAt || 'Online').toLowerCase(),
                    )}
                  </Tag>
                </div>
              </div>
              <div className="text-gray-500 text-xs font-normal">
                {descriptionText}
              </div>
            </Col>
          </Row>
        </div>
      }
      extra={
        canEditProfile && (
          <Dropdown menu={menuProps} trigger={['click']}>
            <Button className="bg-red-900 text-white border-red-900">
              More Actions ▼
            </Button>
          </Dropdown>
        )
      }>
      <Row>
        <Col span={11}>
          <div className="pb-1">
            <div className="py-[1px] flex flex-row">
              <Sprite
                name="account_box"
                className="pt-[2px] w-[20px] h-[20px] text-blue fill-bue"
              />
              <div className="pl-[15px] text-[1rem]">Personal Info</div>
            </div>
          </div>
          <List
            loading={loadingUsers}
            bordered
            dataSource={personalInfo}
            renderItem={(item: RowProps) => (
              <List.Item>
                <Col className="text-gray-500">{item.label}</Col>
                <Col>{item.value}</Col>
              </List.Item>
            )}
          />
          <div className="pb-1 mt-[20px]">
            <div className="py-[1px] flex flex-row">
              <Sprite
                name="store_mall_directory"
                className="pt-[2px] w-[20px] h-[20px] text-blue fill-bue"
              />
              <div className="pl-[15px] text-[1rem]">Address Info</div>
            </div>
          </div>
          <List
            loading={loadingUsers}
            bordered
            dataSource={addressInfo}
            renderItem={(item: RowProps) => (
              <List.Item>
                <Row>
                  <Col span={24} className="text-gray-500">
                    {item.label}
                  </Col>
                  <Col span={24}>{item.value}</Col>
                </Row>
              </List.Item>
            )}
          />
        </Col>
        <Col>
          <Divider
            type="vertical"
            className="mx-[20px]"
            style={{ height: '100%' }}
          />
        </Col>
        <Col span={11}>
          <div className="pb-1">
            <div className="py-[1px] flex flex-row">
              <Sprite
                name="chrome_reader_mode"
                className="pt-[2px] w-[20px] h-[20px] text-blue fill-bue"
              />
              <div className="pl-[15px] text-[1rem]">Account Info</div>
            </div>
          </div>
          <List
            loading={loadingUsers}
            bordered
            dataSource={accountInfo}
            renderItem={(item: RowProps) => (
              <List.Item>
                <Col className="text-gray-500">{item.label}</Col>
                <Col>{item.value}</Col>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </Card>
  );
};
